"use client";

import { useState, useCallback, useEffect } from "react";
import { Input } from "src/components/ui/input";
import { Card, CardContent } from "src/components/ui/card";
import { Button } from "src/components/ui/button";
import { Loader2, Save } from "lucide-react";
import { DatePicker, TimePicker } from "antd";
import { QuestionType } from "src/lib/types";
import { generateId } from "src/lib/utils";
import dayjs from "dayjs";
import { useLanguage } from "../../../../../context/LanguageContext";
import EditorTextArea from "../../../../../components/EditorTextArea";
import { toast } from "sonner";
import QuizBuilder from "../../../../../components/Quiz/quiz-builder";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { BaseUrlApi, ErrorMessage } from "../../../../../lib/api";
import { SubLessonData } from "../../../../../lib/languages";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../../components/ui/select";
import { useAPI } from "../../../../../context/APIContext";

const { RangePicker } = DatePicker;

export default function EditQuiz() {
  const navigate = useNavigate();

  const { language } = useLanguage();
  const t = SubLessonData[language] || SubLessonData["en"];

  const params = useParams();
  const { state } = useLocation();
  const lessonId = state?.lessonId;
  const isTeacher = state?.isTeacher;

  let bid;
  if (isTeacher) {
    bid = state?.bid;
  } else {
    bid = params.bid;
  }

  // Get All Lessons
  const [loadingLessons, setLoadingLessons] = useState(true);
  const [lessons, setLessons] = useState([]);
  const AllLessons = () => {
    axios
      .get(`${BaseUrlApi}/teacher/lessons`)
      .then(({ data }) => {
        setLessons(data.data);
      })
      .catch((error) => {
        toast("Error loading lessons", {
          description: ErrorMessage(error),
        });
      })
      .finally(() => setLoadingLessons(false));
  };

  useEffect(() => {
    AllLessons();
  }, []);

  const [loading, setLoading] = useState(false);
  const [fetchingQuiz, setFetchingQuiz] = useState(true);
  const [errors, setErrors] = useState({});

  // Initialize form with all required fields
  const [form, setForm] = useState({
    title_en: "",
    title_ar: "",
    description_en: "",
    description_ar: "",
    duration: "01:00:00",
    start_date: null,
    end_date: null,
    show_degree: 0,
    show_answer: 0,
    status: false,
    questions: [
      {
        id: generateId(),
        type: QuestionType.MULTIPLE_CHOICE,
        text: "",
        score: "5.00",
        options: [
          { id: generateId(), text: "", isCorrect: false },
          { id: generateId(), text: "", isCorrect: false },
        ],
        file: null,
      },
    ],
    lesson_id: "",
    course_id: "",
    category_id: "",
  });

  // Fetch quiz data
  useEffect(() => {
    const fetchQuiz = async () => {
      try {
        setFetchingQuiz(true);
        const response = await axios.get(`${BaseUrlApi}/quizzes/${bid}`);
        const quizData = response.data.data;

        // Parse title and description from JSON if they are strings
        let title = quizData.title;
        let description = quizData.description;
        if (typeof title === "string") {
          try {
            title = JSON.parse(title);
          } catch (e) {
            title = { en: title, ar: title };
          }
        }
        if (typeof description === "string") {
          try {
            description = JSON.parse(description);
          } catch (e) {
            description = { en: description, ar: description };
          }
        }

        // Format questions
        const formattedQuestions = quizData.questions.map((q) => {
          let questionText = q.text;
          if (typeof questionText === "string") {
            try {
              questionText = JSON.parse(questionText);
            } catch (e) {
              questionText = { en: questionText, ar: questionText };
            }
          }
          const formattedOptions = q.options.map((o) => {
            let optionText = o.text;
            if (typeof optionText === "string") {
              try {
                optionText = JSON.parse(optionText);
              } catch (e) {
                optionText = { en: optionText, ar: optionText };
              }
            }
            return {
              id: o.id || generateId(),
              text: o.text,
              isCorrect: o.isCorrect === 1 || o.isCorrect === true,
              image: o.image || null,
            };
          });
          return {
            id: q.id || generateId(),
            type: q.type || "mcq",
            text: q.text,
            score: q.score || "5.00",
            file: q.file || null,
            options: formattedOptions,
            isCorrect: q.isCorrect === true || q.isCorrect === 1,
          };
        });

        setForm({
          title_en: title?.en || "",
          title_ar: title?.ar || "",
          description_en: description?.en || "",
          description_ar: description?.ar || "",
          duration: quizData.duration || "01:00:00",
          start_date: quizData.start_date,
          end_date: quizData.end_date,
          show_degree: quizData.show_degree === 1 ? 1 : 0,
          show_answer: quizData.show_answer === 1 ? 1 : 0,
          status: quizData.status === 1 || quizData.status === true,
          questions: formattedQuestions,
          lesson_id: quizData.lesson_id || "",
          course_id: quizData.course_id || "",
          category_id: quizData.category_id || "",
        });
      } catch (error) {
        console.error("Error fetching quiz:", error);
        toast(t.error || "Error", {
          description: "Failed to fetch quiz data. Please try again.",
        });
      } finally {
        setFetchingQuiz(false);
      }
    };

    if (bid) {
      fetchQuiz();
    }
  }, [bid]);

  // Handle duration change
  const handleDurationChange = (time, timeString) => {
    setForm((prevForm) => ({ ...prevForm, duration: timeString }));
  };

  // Handle date range change
  const handleDateRangeChange = (dates, dateStrings) => {
    if (dates) {
      setForm((prevForm) => ({
        ...prevForm,
        start_date: dateStrings[0],
        end_date: dateStrings[1],
      }));
    } else {
      setForm((prevForm) => ({
        ...prevForm,
        start_date: null,
        end_date: null,
      }));
    }
  };

  // Update questions from quiz builder – memoized
  const updateQuestions = useCallback((questions) => {
    const formattedQuestions = questions.map((question) => ({
      text: question.text,
      type:
        question.type === QuestionType.MULTIPLE_CHOICE
          ? "mcq"
          : question.type === QuestionType.TRUE_FALSE
          ? "true_false"
          : question.type === QuestionType.PARAGRAPH
          ? "written"
          : "file_upload",
      score: question.score || "5.00",
      file: question.imageUrl,
      options: question.options.map((option) => ({
        text: option.text,
        isCorrect: option.isCorrect ? 1 : 0,
        image: null,
      })),
    }));
    setForm((prevForm) => ({ ...prevForm, questions: formattedQuestions }));
  }, []);

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    if (!form.title_en && !form.title_ar)
      newErrors.title = ["Title is required"];
    if (!form.duration) newErrors.duration = ["Duration is required"];
    if (!form.start_date || !form.end_date)
      newErrors.dates = ["Start and end dates are required"];
    if (form.questions.length === 0)
      newErrors.questions = ["At least one question is required"];
    else {
      const invalidQuestions = form.questions.filter(
        (q) => !q.text || (q.type === "mcq" && q.options.length < 2)
      );
      if (invalidQuestions.length > 0)
        newErrors.questions = ["Some questions are incomplete"];
    }
    return newErrors;
  };

  // Handle form submission
  const handleSubmitQuiz = async () => {
    setLoading(true);
    setErrors({});
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setLoading(false);
      toast(t.validationError, {
        description: t.checkFormForErrors,
      });
      return;
    }

    try {
      const formData = new FormData();
      formData.append(
        "title",
        JSON.stringify({ en: form.title_en, ar: form.title_ar })
      );
      formData.append(
        "description",
        JSON.stringify({ en: form.description_en, ar: form.description_ar })
      );
      formData.append("duration", form.duration);
      formData.append("start_date", form.start_date);
      formData.append("end_date", form.end_date);
      formData.append("show_answer", form.show_answer);
      formData.append("show_degree", form.show_degree);
      formData.append("lesson_id", isTeacher ? form.lesson_id : lessonId);
      if (!!isTeacher) {
        !!form.course_id && formData.append("course_id", form.course_id);
        !!form.category_id && formData.append("category_id", form.category_id);
      }

      form.questions.forEach((question, i) => {
        formData.append(`question[${i}][text]`, question.text || "");
        formData.append(`question[${i}][type]`, question.type || "");
        formData.append(`question[${i}][file]`, question.file || "");
        formData.append(`question[${i}][score]`, question.score || "5.00");
        question.options.forEach((option, j) => {
          formData.append(`question[${i}][options][${j}][text]`, option.text);
          formData.append(
            `question[${i}][options][${j}][isCorrect]`,
            option.isCorrect ? "1" : "0"
          );
        });
      });

      await axios.post(`${BaseUrlApi}/quizzes/update/${bid}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast(t.quizUpdatedToastTitle, {
        description: t.quizUpdatedToastDescription,
      });

      navigate(-1);
    } catch (error) {
      console.error("Error updating quiz:", error);
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
      if (error.response?.status === 401) {
        toast("Authentication Error", {
          description: "Please log in to continue",
        });
        return;
      }
      toast(t.error || "Error", {
        description:
          error.response?.data?.message ||
          "Failed to update quiz. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-10" dir={language === "ar" ? "rtl" : "ltr"}>
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="w-full sm:w-fit">
          <p className="font-bold text-2xl pt-5" id="category">
            {t.editQuizTitle}
          </p>
          <p className="font-medium text-sm text-muted-foreground">
            {t.editQuizDescription}
          </p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-fit">
          <Button
            className="w-full sm:w-fit h-10 bg-blue-600 text-white hover:bg-blue-600/80 rounded-xl"
            onClick={handleSubmitQuiz}
            disabled={loading || fetchingQuiz}
          >
            {loading ? (
              <div className="animate-spin mr-2">
                <Loader2 className="h-4 w-4" />
              </div>
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {t.submitQuiz}
          </Button>
        </div>
      </div>

      {fetchingQuiz ? (
        <div className="flex items-center justify-center p-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <span className="ml-2 text-lg">{t.loadingQuiz}</span>
        </div>
      ) : (
        <>
          <div className="col-span-1 lg:col-span-2">
            <Card className="h-full flex flex-col">
              <CardContent className="p-4 space-y-4 flex-1 flex flex-col">
                <p className="font-bold text-xl border-b pb-2">
                  {t.generalInformation}
                </p>
                <div className="space-y-4 flex-1 flex flex-col">
                  <div className="flex items-center gap-4 flex-col sm:flex-row">
                    <div className="flex flex-col gap-2 w-full">
                      <label htmlFor="title_en" className="text-sm">
                        {t.titleEn}
                      </label>
                      <Input
                        readOnly={loading}
                        id="title_en"
                        name="title_en"
                        type="text"
                        placeholder={t.enterTitleEn}
                        value={form.title_en}
                        onChange={(e) =>
                          setForm((prevForm) => ({
                            ...prevForm,
                            title_en: e.target.value,
                          }))
                        }
                      />
                      {errors?.title && (
                        <p className="text-red-500 font-semibold text-sm">
                          {errors.title[0]}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 w-full">
                      <label htmlFor="title_ar" className="text-sm">
                        {t.titleAr}
                      </label>
                      <Input
                        readOnly={loading}
                        id="title_ar"
                        name="title_ar"
                        type="text"
                        placeholder={t.enterTitleAr}
                        value={form.title_ar}
                        onChange={(e) =>
                          setForm((prevForm) => ({
                            ...prevForm,
                            title_ar: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 flex-1">
                    <label htmlFor="description" className="text-sm">
                      {t.descriptionLabel}
                    </label>
                    <EditorTextArea
                      form={form}
                      setForm={setForm}
                      disabled={loading}
                      en="description_en"
                      ar="description_ar"
                    />
                  </div>
                  <div className="flex items-center gap-4 flex-col sm:flex-row">
                    <div className="flex flex-col gap-2 w-full">
                      <label htmlFor="duration" className="text-sm">
                        {t.durationLabel}
                      </label>
                      <TimePicker
                        className="w-full"
                        value={
                          form.duration
                            ? dayjs(form.duration, "HH:mm:ss")
                            : null
                        }
                        format="HH:mm:ss"
                        onChange={handleDurationChange}
                        disabled={loading}
                      />
                      {errors?.duration && (
                        <p className="text-red-500 font-semibold text-sm">
                          {errors.duration[0]}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 w-full">
                      <label htmlFor="start_date" className="text-sm">
                        {t.dateRangeLabel}
                      </label>
                      <RangePicker
                        className="w-full"
                        showTime={{ format: "HH:mm" }}
                        format="YYYY-MM-DD HH:mm"
                        value={
                          form.start_date && form.end_date
                            ? [dayjs(form.start_date), dayjs(form.end_date)]
                            : null
                        }
                        onChange={handleDateRangeChange}
                        disabled={loading}
                      />
                      {errors?.dates && (
                        <p className="text-red-500 font-semibold text-sm">
                          {errors.dates[0]}
                        </p>
                      )}
                    </div>
                  </div>
                  {/* Show */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card>
                      <CardContent className="p-4 space-y-4">
                        <div className="border-b pb-2">
                          <p className="font-bold text-xl">{t.showAnswer}</p>
                        </div>
                        <div className="grid gap-2">
                          <Select
                            value={+form?.show_answer}
                            onValueChange={(e) =>
                              setForm({ ...form, show_answer: e })
                            }
                          >
                            <SelectTrigger
                              className="rounded-lg sm:ml-auto"
                              aria-label="Select status"
                            >
                              <SelectValue placeholder={t.selectAnswer} />
                            </SelectTrigger>
                            <SelectContent className="rounded-xl">
                              <SelectItem value={1} className="rounded-lg">
                                {t.active}
                              </SelectItem>
                              <SelectItem value={0} className="rounded-lg">
                                {t.inactive}
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {errors?.show_answer && (
                          <p className="text-red-500 font-semibold text-sm">
                            {errors.show_answer[0]}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4 space-y-4">
                        <div className="border-b pb-2">
                          <p className="font-bold text-xl">{t.showDegree}</p>
                        </div>
                        <div className="grid gap-2">
                          <Select
                            value={+form?.show_degree}
                            onValueChange={(e) =>
                              setForm({ ...form, show_degree: e })
                            }
                          >
                            <SelectTrigger
                              className="rounded-lg sm:ml-auto"
                              aria-label="Select status"
                            >
                              <SelectValue placeholder={t.selectDegree} />
                            </SelectTrigger>
                            <SelectContent className="rounded-xl">
                              <SelectItem value={1} className="rounded-lg">
                                {t.active}
                              </SelectItem>
                              <SelectItem value={0} className="rounded-lg">
                                {t.inactive}
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {errors?.show_degree && (
                          <p className="text-red-500 font-semibold text-sm">
                            {errors.show_degree[0]}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                  {/* Select Ids */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {!!isTeacher && (
                      <>
                        {/* Lessons */}
                        <div className="flex flex-col gap-2 w-full">
                          <label htmlFor="due_date" className="text-sm">
                            {t.selectLesson}
                          </label>
                          <Select
                            value={form.lesson_id || ""}
                            onValueChange={(value) =>
                              setForm({
                                ...form,
                                lesson_id: +value,
                                category_id: "",
                                course_id: "",
                              })
                            }
                            className="w-full p-2 border rounded"
                            disabled={loadingLessons}
                          >
                            <SelectTrigger
                              className="rounded-lg"
                              aria-label={t.selectLesson}
                            >
                              <SelectValue placeholder={t.selectLesson} />
                            </SelectTrigger>
                            <SelectContent>
                              {lessons.length !== 0 ? (
                                lessons.map((lesson) => (
                                  <SelectItem key={lesson} value={lesson.id}>
                                    {lesson.title[language] ||
                                      lesson.title.en ||
                                      lesson.title.ar}
                                  </SelectItem>
                                ))
                              ) : (
                                <SelectItem>No lessons found</SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                          {errors?.lesson_id && (
                            <p className="text-red-500 font-semibold text-sm">
                              {errors.lesson_id[0]}
                            </p>
                          )}
                        </div>
                        {/* Categories */}
                        {/* <div className="flex flex-col gap-2 w-full">
                          <label htmlFor="due_date" className="text-sm">
                            {t.selectCategory}
                          </label>
                          <Select
                            value={form.category_id || ""}
                            onValueChange={(value) =>
                              setForm({
                                ...form,
                                category_id: +value,
                                lesson_id: "",
                                course_id: "",
                              })
                            }
                            className="w-full p-2 border rounded"
                            disabled={loadingCategories}
                          >
                            <SelectTrigger
                              className="rounded-lg"
                              aria-label={t.selectCategory}
                            >
                              <SelectValue placeholder={t.selectCategory} />
                            </SelectTrigger>
                            <SelectContent>
                              {categories.map((cat) => (
                                <SelectItem key={cat} value={cat.id}>
                                  {cat.title[language] ||
                                    cat.title.en ||
                                    cat.title.ar}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {errors?.category_id && (
                            <p className="text-red-500 font-semibold text-sm">
                              {errors.category_id[0]}
                            </p>
                          )}
                        </div> */}
                        {/* Courses */}
                        {/* <div className="flex flex-col gap-2 w-full">
                          <label htmlFor="due_date" className="text-sm">
                            {t.selectCourse}
                          </label>
                          <Select
                            value={form.course_id || ""}
                            onValueChange={(value) =>
                              setForm({
                                ...form,
                                course_id: +value,
                                category_id: "",
                                lesson_id: "",
                              })
                            }
                            className="w-full p-2 border rounded"
                            disabled={loadingCourses}
                          >
                            <SelectTrigger
                              className="rounded-lg"
                              aria-label={t.selectCourse}
                            >
                              <SelectValue placeholder={t.selectCourse} />
                            </SelectTrigger>
                            <SelectContent>
                              {courses.map((course) => (
                                <SelectItem key={course} value={course.id}>
                                  {course.title[language] ||
                                    course.title.en ||
                                    course.title.ar}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {errors?.course_id && (
                            <p className="text-red-500 font-semibold text-sm">
                              {errors.course_id[0]}
                            </p>
                          )}
                        </div> */}
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quiz Builder Section */}
          <div className="col-span-1 lg:col-span-2">
            <Card className="h-full flex flex-col">
              <CardContent className="p-4">
                <p className="font-bold text-xl border-b pb-2 mb-4">
                  {language === "ar" ? "أسئلة الاختبار" : "Quiz Questions"}
                </p>
                <QuizBuilder
                  initialQuestions={form.questions.map((q) => ({
                    id: q.id,
                    type:
                      q.type === "mcq"
                        ? QuestionType.MULTIPLE_CHOICE
                        : q.type === "true_false"
                        ? QuestionType.TRUE_FALSE
                        : q.type === "written"
                        ? QuestionType.PARAGRAPH
                        : QuestionType.FILE_UPLOAD,
                    text: q.text || "",
                    score: q.score,
                    options: q.options.map((o) => ({
                      id: o.id,
                      text: o.text || "",
                      isCorrect: o.isCorrect === 1 || o.isCorrect === true,
                      image: o.image,
                    })),
                    imageUrl: q.file,
                  }))}
                  onQuestionsChange={updateQuestions}
                  disabled={loading}
                />
                {errors?.questions && (
                  <p className="text-red-500 font-semibold text-sm mt-2">
                    {errors.questions[0]}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
